import type { Contact, ContactAiReviewRecommendation } from '@/types/email';

type Handler<Payload> = (payload: Payload) => void;

type MailboxReloadEvent = {
  accountId: string;
  folderId: string;
  options?: { reset?: boolean };
};

type ContactStateUpdateEvent = {
  contactId: string;
  approvedState: Contact['approvedState'];
  notificationsEnabled?: Contact['notificationsEnabled'];
};

type ContactReviewUpdateEvent = {
  contactId: string;
  recommendation: ContactAiReviewRecommendation | null;
};

type ThreadRefreshEvent = {
  threadId: string;
  accountId: string;
};

export type MailEventMap = {
  'mail:selection:clear': void;
  'mail:selectAllVisible': void;
  'mail:senderGrouping:reset': void;
  'mail:mailbox:reload': MailboxReloadEvent;
  'mail:contact:stateChanged': ContactStateUpdateEvent;
  'mail:contact:reviewUpdated': ContactReviewUpdateEvent;
  'mail:thread:refresh': ThreadRefreshEvent;
};

export class MailEventBus<Events extends Record<string, unknown>> {
  private listeners = new Map<keyof Events, Set<Handler<Events[keyof Events]>>>();

  on<EventName extends keyof Events>(event: EventName, handler: Handler<Events[EventName]>) {
    const existing = this.listeners.get(event);
    if (existing) {
      existing.add(handler as Handler<Events[keyof Events]>);
    } else {
      this.listeners.set(event, new Set([handler as Handler<Events[keyof Events]>]));
    }
    return () => this.off(event, handler);
  }

  off<EventName extends keyof Events>(event: EventName, handler: Handler<Events[EventName]>) {
    const existing = this.listeners.get(event);
    existing?.delete(handler as Handler<Events[keyof Events]>);
  }

  emit<EventName extends keyof Events>(event: EventName, payload: Events[EventName]) {
    const handlers = this.listeners.get(event);
    handlers?.forEach((handler) => {
      handler(payload);
    });
  }
}

export const mailEvents = new MailEventBus<MailEventMap>();
